import { MessagesSquare } from 'lucide-react';
import { externalLinkAvailable, tryOpenExternalLink } from '../discord/externalLink.js';
import { trackClient } from '../net/telemetry.js';

/**
 * CommunityCTA — the way out for a player who has nobody to summon.
 *
 * The measured #1 leak is not "groups that can't reach five", it is arrivals
 * with no group at all: 79 lobbies stalled without ever starting in the 28 days
 * to 2026-08-27, **61 of them solo**. Everything the app offered those players
 * ("Summon your guild") assumes friends already in the channel. This offers the
 * other half — the official Ringmaster server, where games are hosted regularly
 * and other players are looking for a table.
 *
 * Deliberately ONE affordance, worded once, on every surface it appears
 * (`COMMUNITY_CTA_LABEL` — same doctrine as `biggerShowLabel`). It is always an
 * `<a>`, never a `<button>`: it leaves the app rather than acting on the game,
 * and keeping it out of the action clusters is what lets it sit beside the
 * invite CTA and the END actions without crowding them (see R-001).
 *
 * Two tiers, no dead states:
 *   1. Inside the Activity → `sdk.commands.openExternalLink` (discord/externalLink.ts),
 *      which is the ONLY way out of the iframe; the anchor's default is suppressed.
 *   2. Anywhere else (Store, /play, ?discordmock=1) → the anchor's own
 *      `target="_blank"`, no JS required.
 *
 * The click is beaconed as `community_cta_clicked` with the surface. NOTE that
 * beacon needs a room + session token (net/telemetry.ts), so the Store surface —
 * a plain browser visitor who is in no room — is deliberately UNMEASURED here
 * rather than widening `/api/track`'s seated-player auth.
 */

/** The official Ringmaster Discord. Owner-supplied permanent invite (issue #38). */
export const COMMUNITY_INVITE_URL = 'https://discord.gg/GKXsNxGHEB';

/** One label for every surface — the same offer, worded once. */
export const COMMUNITY_CTA_LABEL = 'Find players in our Discord';

/** The honest half of the pitch, in the owner's own terms (issue #38). */
export const COMMUNITY_CTA_NOTE = 'We host games regularly — come find a table.';

/** Which surface a given CTA is mounted on — rides along on the beacon. */
export type CommunitySurface = 'stall' | 'end' | 'store';

export function CommunityCTA({
  surface,
  /** Extra root styles (surface spacing/stacking). */
  style,
}: {
  surface: CommunitySurface;
  style?: React.CSSProperties;
}) {
  function onClick(e: React.MouseEvent<HTMLAnchorElement>) {
    trackClient('community_cta_clicked', { surface });
    // Inside Discord the anchor's own navigation is sandboxed away, so the SDK
    // command is the real click. Outside, we let the browser do its job.
    if (externalLinkAvailable()) {
      e.preventDefault();
      void tryOpenExternalLink(COMMUNITY_INVITE_URL);
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        textAlign: 'center',
        ...style,
      }}
    >
      <a
        className="rm-hover"
        href={COMMUNITY_INVITE_URL}
        target="_blank"
        rel="noreferrer noopener"
        onClick={onClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: 'var(--space-1) var(--space-2)',
          fontFamily: 'var(--font-ui)',
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textDecoration: 'none',
        }}
      >
        <MessagesSquare size={14} aria-hidden />
        <span>{COMMUNITY_CTA_LABEL}</span>
        <span aria-hidden>→</span>
      </a>
      <span
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 11,
          lineHeight: 1.4,
          color: 'var(--text-tertiary)',
        }}
      >
        {COMMUNITY_CTA_NOTE}
      </span>
    </div>
  );
}
